import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { FaDownload, FaFileImport } from 'react-icons/fa';
import { MdErrorOutline, MdOutlineDoneAll } from 'react-icons/md';
import { LuSearchCheck } from 'react-icons/lu';
import { Button, Drawer } from 'components/common';
import { downloadImportTemplate, importLogisticTrackings } from 'redux/crm/logistic/logisticTrackingSlice';
import ImportResultTable from './ImportResultTable';

const MAX_BYTES = 10 * 1024 * 1024;

// Two steps on purpose: "Check file" runs every check on the server and shows what
// WOULD change, writing nothing; "Import" then writes it. A whole column pasted one
// row off is caught at the preview, not after it has overwritten 200 shipments.
const ImportLogisticTrackingDrawer = ({ show, onHide, templateParams, onImported }) => {
  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError('');
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onHide();
  };

  const handleFileChange = (event) => {
    const selected = event.target.files[0];
    event.target.value = '';
    setPreview(null);
    setResult(null);
    if (!selected) return;
    if (!selected.name.toLowerCase().endsWith('.xlsx')) {
      setError('Please choose an .xlsx file (the downloaded template).');
      setFile(null);
      return;
    }
    if (selected.size > MAX_BYTES) {
      setError('The file is larger than 10 MB.');
      setFile(null);
      return;
    }
    setError('');
    setFile(selected);
  };

  const handleTemplate = async (blank) => {
    setBusy(blank ? 'blank' : 'template');
    try {
      await dispatch(downloadImportTemplate(blank ? { blank: true } : templateParams));
    } finally {
      setBusy('');
    }
  };

  const run = async (dryRun) => {
    setBusy(dryRun ? 'check' : 'import');
    setError('');
    try {
      const data = await dispatch(importLogisticTrackings({ file, dryRun })).unwrap();
      if (dryRun) {
        setPreview(data);
      } else {
        setResult(data);
        if (writeCount(data.summary) > 0) onImported();
      }
    } catch (err) {
      setError(err?.importError || 'Import failed. Please try again.');
    } finally {
      setBusy('');
    }
  };

  const shown = result || preview;
  const pending = preview ? writeCount(preview.summary) : 0;
  const canImport = !result && pending > 0;

  const footer = (
    <div className="tw-flex tw-justify-end tw-gap-2 tw-w-full">
      {result ? (
        <>
          <Button variant="ghost" size="sm" onClick={handleClose}>Close</Button>
          <Button variant="primary" size="sm" onClick={reset}>
            <FaFileImport size={12} /> Import another file
          </Button>
        </>
      ) : (
        <>
          <Button variant="ghost" size="sm" onClick={handleClose} disabled={!!busy}>Cancel</Button>
          <Button variant="secondary" size="sm" onClick={() => run(true)} disabled={!file || !!busy}>
            <LuSearchCheck size={13} /> {busy === 'check' ? 'Checking…' : 'Check file'}
          </Button>
          <Button variant="primary" size="sm" onClick={() => run(false)} disabled={!canImport || !!busy}>
            <FaFileImport size={12} />
            {busy === 'import' ? 'Importing…' : `Import ${canImport ? pending : ''} row${pending === 1 ? '' : 's'}`}
          </Button>
        </>
      )}
    </div>
  );

  return (
    <Drawer
      show={show}
      onHide={handleClose}
      backdropClick={false}
      width="760px"
      minWidth="420px"
      title={<span className="tw-flex tw-items-center tw-gap-2"><FaFileImport size={14} className="tw-text-primary-600" /> Import Logistic Tracking</span>}
      footer={footer}
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        {!result && (
          <>
            <Step number={1} title="Download a template">
              <div className="tw-grid sm:tw-grid-cols-2 tw-gap-2">
                <TemplateChoice
                  title="Shipments in the list"
                  text="Pre-filled with the shipments matching your current filters. Fill in the blue columns."
                  busy={busy === 'template'}
                  disabled={!!busy}
                  onClick={() => handleTemplate(false)}
                />
                <TemplateChoice
                  title="Blank — old shipments"
                  text="For departed shipments not in the list yet: type Job no., Building, Phase name and the Actual ETD."
                  busy={busy === 'blank'}
                  disabled={!!busy}
                  onClick={() => handleTemplate(true)}
                />
              </div>
              <p className="tw-text-xs tw-text-slate-500 tw-mt-2 tw-mb-0">
                Either file can mix both: rows are matched by Job no. + Building + Phase name. An empty cell keeps the current value.
              </p>
            </Step>

            <Step number={2} title="Upload the filled file">
              <input
                type="file"
                accept=".xlsx"
                onChange={handleFileChange}
                disabled={!!busy}
                aria-label="Excel file to import"
                className="tw-block tw-w-full tw-text-sm tw-text-slate-600 file:tw-mr-3 file:tw-py-1.5 file:tw-px-3 file:tw-rounded file:tw-border-0 file:tw-bg-primary-50 file:tw-text-primary-700 file:tw-font-semibold hover:file:tw-bg-primary-100 tw-cursor-pointer"
              />
              {file && <p className="tw-mt-2 tw-mb-0 tw-text-xs tw-text-emerald-600 tw-font-semibold">✓ {file.name}</p>}
              <p className="tw-mt-1 tw-mb-0 tw-text-xs tw-text-slate-400">.xlsx only · max 10 MB · up to 2,000 rows</p>
            </Step>
          </>
        )}

        {error && (
          <div role="alert" className="tw-flex tw-items-center tw-gap-2 tw-bg-red-50 tw-border tw-border-solid tw-border-red-200 tw-text-red-700 tw-rounded tw-px-3 tw-py-2 tw-text-sm">
            <MdErrorOutline size={18} className="tw-flex-shrink-0" /> {error}
          </div>
        )}

        {shown && <ResultSection data={shown} done={!!result} />}
      </div>
    </Drawer>
  );
};

const writeCount = summary => (summary?.changed || 0) + (summary?.created || 0);

const TemplateChoice = ({ title, text, busy, disabled, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="tw-flex tw-flex-col tw-items-start tw-gap-1 tw-text-left tw-rounded tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-p-3 tw-cursor-pointer hover:tw-border-primary-400 hover:tw-bg-primary-50/40 disabled:tw-opacity-60 disabled:tw-cursor-default focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-500"
  >
    <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-semibold tw-text-primary-700">
      <FaDownload size={11} aria-hidden="true" /> {busy ? 'Preparing…' : title}
    </span>
    <span className="tw-text-xs tw-text-slate-500">{text}</span>
  </button>
);

TemplateChoice.propTypes = {
  title: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  busy: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

const Step = ({ number, title, children }) => (
  <section className="tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-p-4">
    <h5 className="tw-mb-2 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-bold tw-text-slate-700">
      <span className="tw-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-xl tw-bg-primary-600 tw-text-white tw-text-[11px]">{number}</span>
      {title}
    </h5>
    {children}
  </section>
);

Step.propTypes = {
  number: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const ResultSection = ({ data, done }) => {
  const { summary } = data;
  const tiles = [
    { label: 'Rows in file', value: summary.total_rows, cls: 'tw-text-slate-700' },
    { label: done ? 'Updated' : 'Will update', value: summary.changed, cls: 'tw-text-emerald-600' },
    { label: done ? 'Added' : 'Will add', value: summary.created || 0, cls: 'tw-text-primary-600' },
    { label: 'No change', value: summary.unchanged, cls: 'tw-text-slate-500' },
    { label: 'Errors (skipped)', value: summary.failed, cls: summary.failed ? 'tw-text-red-600' : 'tw-text-slate-500' },
  ];

  return (
    <section className="tw-flex tw-flex-col tw-gap-3" aria-live="polite">
      <h5 className="tw-mb-0 tw-text-sm tw-font-bold tw-text-slate-700">
        {done ? 'Import finished' : 'Check result — nothing has been saved yet'}
      </h5>
      <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-5 tw-gap-2">
        {tiles.map(({ label, value, cls }) => (
          <div key={label} className="tw-rounded tw-border tw-border-solid tw-border-slate-200 tw-bg-slate-50 tw-p-2.5">
            <p className="tw-text-[11px] tw-text-slate-500 tw-mb-0.5">{label}</p>
            <p className={`tw-text-lg tw-font-bold tw-mb-0 tw-tabular-nums ${cls}`}>{value}</p>
          </div>
        ))}
      </div>
      {done && summary.failed === 0 && (
        <p className="tw-flex tw-items-center tw-gap-2 tw-text-emerald-600 tw-text-sm tw-font-semibold tw-mb-0">
          <MdOutlineDoneAll size={18} /> All rows imported.
        </p>
      )}
      {!done && writeCount(summary) === 0 && summary.failed === 0 && (
        <p className="tw-text-sm tw-text-slate-500 tw-mb-0">Nothing to import — every row already matches.</p>
      )}
      <ImportResultTable rows={data.rows} />
    </section>
  );
};

ResultSection.propTypes = {
  data: PropTypes.shape({
    summary: PropTypes.object.isRequired,
    rows: PropTypes.array.isRequired,
  }).isRequired,
  done: PropTypes.bool,
};

ImportLogisticTrackingDrawer.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  templateParams: PropTypes.object,
  onImported: PropTypes.func,
};

ImportLogisticTrackingDrawer.defaultProps = {
  templateParams: {},
  onImported: () => {},
};

export default ImportLogisticTrackingDrawer;
